// Pure helpers for parameter mappings — no React imports, jest-testable.

import { JsonValue, ParameterMapping } from '../types'

export type FieldMode = 'fixed' | 'expression'

export const valueToText = (value: JsonValue | undefined): string => {
  if (value === undefined) return ''
  if (typeof value === 'string') return value
  return JSON.stringify(value, null, 2)
}

// A non-string value can only have come from JSON mode.
export const isJsonMode = (mapping?: ParameterMapping): boolean =>
  mapping?.jsonMode === true || (mapping !== undefined && typeof mapping.value !== 'string')

// JSON-shaped text (object/array) must not auto-switch to expression mode:
// stored as an expression string it would reach the tool as a string.
export const looksLikeJsonTemplate = (text: string): boolean => {
  const trimmed = text.trimStart()
  return (trimmed.startsWith('{') && !trimmed.startsWith('{{')) || trimmed.startsWith('[')
}

// Mappings saved with source "hardcoded" may still hold expressions — detect
// {{ so they open in expression mode; JSON-shaped values stay fixed.
export const deriveFieldMode = (mapping?: ParameterMapping): FieldMode => {
  if (mapping?.source === 'expression') return 'expression'
  if (
    typeof mapping?.value === 'string' &&
    mapping.value.includes('{{') &&
    !looksLikeJsonTemplate(mapping.value)
  ) {
    return 'expression'
  }
  return 'fixed'
}

// Mirror of workflow_domain._extract_field_paths — keep in lockstep.
export const extractFieldPaths = (obj: JsonValue, prefix = ''): string[] => {
  const paths: string[] = []
  if (obj !== null && typeof obj === 'object' && !Array.isArray(obj)) {
    for (const [key, val] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${key}` : key
      paths.push(path)
      if (paths.length < 100) paths.push(...extractFieldPaths(val, path))
    }
  } else if (Array.isArray(obj) && obj.length > 0) {
    const path = prefix ? `${prefix}.0` : '0'
    paths.push(path)
    if (paths.length < 100) paths.push(...extractFieldPaths(obj[0], path))
  }
  return paths.slice(0, 100)
}

// Drop mappings the new tool doesn't declare — the executor sends every mapping.
export const filterParamsForTool = (
  params: Record<string, ParameterMapping>,
  declaredNames: Set<string>,
): { kept: Record<string, ParameterMapping>; removedCount: number } => {
  const kept: Record<string, ParameterMapping> = {}
  let removedCount = 0
  Object.entries(params).forEach(([name, mapping]) => {
    if (declaredNames.has(name)) {
      kept[name] = mapping // eslint-disable-line security/detect-object-injection
    } else {
      removedCount += 1
    }
  })
  return { kept, removedCount }
}
